import { AdvancedSelect } from '../../../../../widgets/select';
import { Checkbox } from '../../../../../widgets/checkbox';
import { useDialogManager } from '../../../../../widgets/dialog';
import { NotificationBanner } from '../../../../../widgets/notificationBanner';
import Stack from '../../../../../widgets/stack/stack';
import React, { FC, useCallback, useEffect, useState } from 'react';
import Slider from 'src/components/widgets/slider';
import { UpgradeDialog } from 'src/components/widgets/upgradeDialog/upgradeDialog';
import { stringFormat, T } from 'src/shared/helpers';
import { useSubscriptionData } from 'src/shared/hooks/useSubscriptionData';
import styled from 'styled-components';
import { PropertiesPanelColorControl } from '../scene-properties-panel/propertiesPanelColorProperty';
import { SettingControl, SettingLabel, SettingRow, SettingsSection, SettingsSectionTitle } from '../sceneEditorSettingsLayout';

interface SettingsEditorProps {
    sceneEditor: any;
    zkScene: any;
    section: 'rendering' | 'behaviours';
    isAIEnabled?: boolean;
    setAIEnabled?: (enabled: boolean) => void;
    additionalFeatures?: any;
    /** Gli Info points sono della scena 3D: il Composer non li mostra. */
    showInfoPoints?: boolean;
    /** La scena è cambiata: chi ci sta sopra segni che c’è da salvare. */
    onSceneChanged?: () => void;
}

const TooltipLink = styled.a`
    color: ${(props) => props.theme.app.component.link.color};
    text-decoration: underline;
`;

type InfoPointsColorKey = 'color' | 'borderColor' | 'highlightColor' | 'innerPointColor';

const SettingsEditor: FC<SettingsEditorProps> = ({
    sceneEditor,
    zkScene,
    isAIEnabled,
    setAIEnabled,
    additionalFeatures,
    section,
    showInfoPoints = false,
    onSceneChanged
}) => {
    // L'AMBIENTE HA TRASLOCATO. Texture d'ambiente, skybox e la sua sfocatura stavano qui
    // perché non avevano altro posto; ora la loro casa è la sezione **World**, dove stanno
    // insieme allo sfondo e al piano — cioè insieme alle altre cose che circondano il
    // modello. La procedura di caricamento è in `hooks/useEnvironmentTextures`.
    //
    // Resta ospite «Info Points»: la sua casa sarebbero stati i trigger point
    // (docs/scene-editor-new-sections.md § 3), ma verranno deprecati e fino ad allora restano
    // qui con il loro avviso. Accanto, il generatore AI: era l'unica voce di Composer.
    const showBehaviours = section === 'behaviours';
    const showRendering = section === 'rendering';
    const { showDialog } = useDialogManager();
    const { sellerMarketplace, updateSubscription } = useSubscriptionData();

    // State for scene configuration
    const [infoPointsColors, setInfoPointsColors] = useState<Record<InfoPointsColorKey, string>>({
        color: '#ffffff',
        borderColor: '#ffffff',
        highlightColor: '#ff0000',
        innerPointColor: '#000000'
    });
    const [webpCompressionType, setWebpCompressionType] = useState<string>('Lossy');
    const [webpCompressionQuality, setWebpCompressionQuality] = useState<number>(80);
    const [enableAI, setEnableAI] = useState<boolean>(false);
    const [optimizeTextureManagement, setOptimizeTextureManagement] = useState<boolean>(false);
    const [enableWebP, setEnableWebP] = useState<boolean>(false);
    const [useDelayedMaterialsLoading, setUseDelayedMaterialsLoading] = useState<boolean>(false);
    const [infoPointsEnabled, setInfoPointsEnabled] = useState<boolean>(false);
    const [autoRotate, setAutoRotateState] = useState<boolean>(false);

    // LA ROTAZIONE AUTOMATICA È UN COMPORTAMENTO DEL NEGOZIO (utente, 2026-10-15): nel configuratore del
    // cliente la camera gira intorno al prodotto; nell'editor no. Fra le proprietà della camera sembrava un
    // interruttore rotto, perché acceso non succedeva niente: qui sta con gli altri comportamenti, e lo dice.
    // Il motore ha solo il setter, e il valore si legge dalla configurazione della camera nella scena.
    const setAutoRotate = useCallback(
        (enabled: boolean) => {
            setAutoRotateState(enabled);
            sceneEditor?.setViewerCameraAutoRotate?.(enabled);
            onSceneChanged?.();
        },
        [sceneEditor, onSceneChanged]
    );

    // Helper functions seguendo la logica del vecchio SceneEditorPage
    const setWebPEnabled = useCallback(
        (enabled: boolean) => {
            setEnableWebP(enabled);
            zkScene?.setWebPEnabled?.(enabled);
            onSceneChanged?.();
        },
        [zkScene, onSceneChanged]
    );

    const setWebPType = useCallback(
        (type: string) => {
            setWebpCompressionType(type);
            zkScene?.setWebPType?.(type);
            onSceneChanged?.();
        },
        [zkScene, onSceneChanged]
    );

    const setWebPQuality = useCallback(
        (quality: number) => {
            setWebpCompressionQuality(quality);
            zkScene?.setWebPQuality?.(quality);
            onSceneChanged?.();
        },
        [zkScene, onSceneChanged]
    );

    const setMatchSameNameTextures = useCallback(
        (enabled: boolean) => {
            setOptimizeTextureManagement(enabled);
            sceneEditor?.setMatchSameNameTextures?.(enabled);
            onSceneChanged?.();
        },
        [sceneEditor, onSceneChanged]
    );

    const setUseDelayedMaterialLoading = useCallback(
        (enabled: boolean) => {
            setUseDelayedMaterialsLoading(enabled);
            sceneEditor?.setUseDelayedMaterialLoading?.(enabled);
            onSceneChanged?.();
        },
        [sceneEditor, onSceneChanged]
    );

    const setInfoPointsEnabledHandler = useCallback(
        (enabled: boolean) => {
            setInfoPointsEnabled(enabled);
            sceneEditor!.setInfoPointsEnabled(enabled);
            onSceneChanged?.();
        },
        [sceneEditor, onSceneChanged]
    );

    const setAiConfigurationEnabled = useCallback(
        (enabled: boolean) => {
            // Verifica se la feature AI Powered Tools è disponibile
            if (enabled && !additionalFeatures?.aiPoweredTools) {
                showDialog(
                    'Upgrade_Plan',
                    <UpgradeDialog
                        marketPlace={sellerMarketplace ?? undefined}
                        message={stringFormat(T._('Your current plan does not include this feature. Upgrade now to access it.', 'Admin'), 'AI Powered Tools')}
                        onUpgradeCompleted={async () => {
                            updateSubscription();
                        }}
                    />
                );
                return;
            }

            setEnableAI(enabled);
            setAIEnabled?.(enabled);
            onSceneChanged?.();
        },
        [additionalFeatures, showDialog, sellerMarketplace, updateSubscription, setAIEnabled, onSceneChanged]
    );

    // Load scene configuration
    useEffect(() => {
        if (sceneEditor && zkScene) {
            try {
                const config = sceneEditor.getSceneConfig();

                // Load current values from backbone model
                if (config) {
                    // Info Points
                    setInfoPointsEnabled(config.get('infoPoints')?.get('enabled') ?? true);
                    setAutoRotateState(config.get('camera')?.get('autoRotate') ?? false);
                    const infoPointsConfig = config.get('infoPoints')?.get('pointsStyle');
                    if (infoPointsConfig) {
                        setInfoPointsColors({
                            color: infoPointsConfig.get?.('color') || '#ffffff',
                            borderColor: infoPointsConfig.get?.('borderColor') || '#ffffff',
                            highlightColor: infoPointsConfig.get?.('highlightColor') || '#ff0000',
                            innerPointColor: infoPointsConfig.get?.('innerPointColor') || '#000000'
                        });
                    }

                    // WebP Compression - usando zkScene
                    setEnableWebP(zkScene?.getWebPEnabled?.() ?? false);
                    setWebpCompressionType(zkScene?.getWebPType?.() ?? 'Lossy');
                    setWebpCompressionQuality(zkScene?.getWebPQuality?.() ?? 80);

                    // Altri settings usando sceneEditor
                    setOptimizeTextureManagement(sceneEditor?.getMatchSameNameTextures?.() ?? false);
                    setUseDelayedMaterialsLoading(sceneEditor?.getUseDelayedMaterialLoading?.() ?? false);

                    setEnableAI(isAIEnabled ?? true);
                }
            } catch (error) {}
        }
    }, [sceneEditor, zkScene]);

    const handleInfoPointsColorChange = useCallback(
        (colorType: InfoPointsColorKey, color: string) => {
            setInfoPointsColors((current) => ({ ...current, [colorType]: color }));
            switch (colorType) {
                case 'color':
                    sceneEditor?.setInfoPointsColor?.(color);
                    break;
                case 'borderColor':
                    sceneEditor?.setInfoPointsBorderColor?.(color);
                    break;
                case 'highlightColor':
                    sceneEditor?.setInfoPointsHighlightColor?.(color);
                    break;
                case 'innerPointColor':
                    sceneEditor?.setInfoPointsInnerPointColor?.(color);
                    break;
            }
            onSceneChanged?.();
        },
        [sceneEditor, onSceneChanged]
    );

    const infoPointsColorRows: { key: InfoPointsColorKey; label: string }[] = [
        { key: 'color', label: T._('Color', 'Admin') },
        { key: 'borderColor', label: T._('Border color', 'Admin') },
        { key: 'highlightColor', label: T._('Highlight color', 'Admin') },
        { key: 'innerPointColor', label: T._('Inner point color', 'Admin') }
    ];

    return (
        <Stack $gap='24px'>
            {/* L'ANTIALIASING NON ABITA PIÙ QUI (utente, 2026-09-22): sta in Effects, nella sezione
                Quality, accanto a glow, tone mapping e color grading — è una cosa che succede
                all'immagine, non una scelta di caricamento. Qui restano le scelte che costano tempo
                di CARICAMENTO, ed è per questo che la categoria adesso si chiama «Performance». */}
            {showRendering && (
                <SettingsSection>
                    <SettingsSectionTitle>{T._('Optimization', 'Admin')}</SettingsSectionTitle>
                    <SettingRow>
                        <Checkbox
                            label={T._('Optimize texture management', 'Admin')}
                            checked={optimizeTextureManagement}
                            onChange={setMatchSameNameTextures}
                            helpTooltip={T._(
                                'Effortlessly merge textures with identical names into a single texture to optimize performance. When you upload the textures with the same names on multiple materials, Zakeke automatically consolidates them, allowing the browser to download it just once. This optimization minimizes loading times and boosts overall performance, providing a faster and smoother 3D product experience for your customers.',
                                'Admin'
                            )}
                        />
                    </SettingRow>
                    <SettingRow>
                        <Checkbox
                            label={T._('WebP textures', 'Admin')}
                            checked={enableWebP}
                            onChange={setWebPEnabled}
                            helpTooltip={
                                <span>
                                    {T._('Enable WebP to optimize texture file sizes. You have two options: ', 'Composer')}
                                    <div>
                                        <strong>{T._('• Lossy: ', 'Composer')}</strong>
                                        {T._('Heavily compresses textures with some quality loss. You can customize the quality level.', 'Composer')}
                                    </div>
                                    <div>
                                        <strong>{T._('• Lossless: ', 'Composer')}</strong>
                                        {T._('Minimally compresses textures to maintain original quality.', 'Composer')}
                                    </div>
                                    <TooltipLink target='_blank' href='https://zakeke.zendesk.com/hc/en-us/articles/14248548502940-WebP-for-Textures'>
                                        {T._('We recommend delving deeper here.', 'Composer')}
                                    </TooltipLink>
                                </span>
                            }
                        />
                    </SettingRow>
                    {enableWebP && (
                        <>
                            <SettingRow>
                                <SettingLabel>{T._('Compression type', 'Admin')}</SettingLabel>
                                <SettingControl>
                                    <AdvancedSelect
                                        value={{ value: webpCompressionType, label: T._(webpCompressionType, 'Admin') }}
                                        options={[
                                            { label: T._('Lossy', 'Admin'), value: 'Lossy' },
                                            { label: T._('Lossless', 'Admin'), value: 'Lossless' }
                                        ]}
                                        onChange={(value) => value && setWebPType(value.value)}
                                    />
                                </SettingControl>
                            </SettingRow>
                            {webpCompressionType === 'Lossy' && (
                                <SettingRow>
                                    <SettingLabel>{T._('Quality', 'Admin')}</SettingLabel>
                                    <SettingControl>
                                        <Slider min={1} max={100} value={webpCompressionQuality} onChange={(val) => setWebPQuality(Number(val))} />
                                    </SettingControl>
                                </SettingRow>
                            )}
                        </>
                    )}
                    <SettingRow>
                        <Checkbox
                            label={T._('Delayed materials loading', 'Admin')}
                            checked={useDelayedMaterialsLoading}
                            onChange={setUseDelayedMaterialLoading}
                        />
                    </SettingRow>
                </SettingsSection>
            )}

            {showBehaviours && (
                <>
                    <SettingsSection>
                        <SettingsSectionTitle>{T._('Storefront', 'Admin')}</SettingsSectionTitle>
                        <SettingRow>
                            <Checkbox
                                label={T._('Auto rotate', 'Admin')}
                                checked={autoRotate}
                                onChange={setAutoRotate}
                                helpTooltip={T._(
                                    'In the storefront the camera turns slowly around the product until the buyer touches it. It does not turn here in the editor.',
                                    'Admin'
                                )}
                            />
                        </SettingRow>
                    </SettingsSection>

                    <SettingsSection>
                        <SettingsSectionTitle>{T._('Automation', 'Admin')}</SettingsSectionTitle>
                        <SettingRow>
                            <Checkbox
                                label={T._('AI product configuration generator', 'Admin')}
                                checked={enableAI}
                                onChange={setAiConfigurationEnabled}
                                helpTooltip={
                                    <>
                                        {T._(
                                            "Enhance your product customization experience with our AI-Powered Configuration Generator. Your clients simply describe their desired configuration and our advanced AI technology automatically generates the perfect customized product for them. No manual option selection, our intelligent system handles the permutations and delivers a tailored configuration that precisely matches your customers' preferences.",
                                            'Composer'
                                        )}
                                        <br />
                                        <TooltipLink target='_blank' href='https://zakeke.zendesk.com/hc/en-us/articles/9649789714460-AI-Configuration'>
                                            {T._('Learn more', 'Composer')}
                                        </TooltipLink>
                                    </>
                                }
                            />
                        </SettingRow>
                    </SettingsSection>

                    {showInfoPoints && (
                        <SettingsSection>
                            <SettingsSectionTitle>{T._('Info points', 'Admin')}</SettingsSectionTitle>
                            <Stack $gap='8px'>
                                <NotificationBanner
                                    type='DEPRECATED'
                                    title={T._('Deprecated', 'Admin')}
                                    content={T._('Info points will be deprecated soon, so we recommend not using them.', 'Admin')}
                                />
                                <div>
                                    <SettingRow>
                                        <Checkbox
                                            label={T._('Info points', 'Admin')}
                                            checked={infoPointsEnabled}
                                            onChange={setInfoPointsEnabledHandler}
                                            helpTooltip={
                                                <>
                                                    {T._('Enable your customers to see high-res 2D images of the product on 3D model. ', 'Composer')}
                                                    <br />
                                                    <TooltipLink href='https://zakeke.zendesk.com/hc/en-us/articles/360014772579' target='_blank'>
                                                        {T._('Learn more', 'Composer')}
                                                    </TooltipLink>
                                                </>
                                            }
                                        />
                                    </SettingRow>
                                    {infoPointsEnabled &&
                                        infoPointsColorRows.map(({ key, label }) => (
                                            <SettingRow key={key}>
                                                <SettingLabel htmlFor={`settings-info-points-${key}`}>{label}</SettingLabel>
                                                <SettingControl>
                                                    <PropertiesPanelColorControl
                                                        id={`settings-info-points-${key}`}
                                                        value={infoPointsColors[key]}
                                                        onChange={(color) => handleInfoPointsColorChange(key, color)}
                                                    />
                                                </SettingControl>
                                            </SettingRow>
                                        ))}
                                </div>
                            </Stack>
                        </SettingsSection>
                    )}
                </>
            )}
        </Stack>
    );
};

export default SettingsEditor;
